<!-- eslint-disable vue/no-v-html -->
<template>
  <b-form-group
    :class="['formfield', groupClass]"
    :state="state"
  >
    <template #label>
      <span v-if="type !== 'toggle' && displayLabel">{{ displayLabel }}<span
        v-if="isRequired"
        class="text-danger ml-1"
        style="font-size: 1.3em; line-height: 0; vertical-align: middle;"
        aria-hidden="true"
      >*</span><span
        v-if="isRequiredOnComplete && !isRequired"
        class="text-warning ml-1"
        style="font-size: 1.3em; line-height: 0; vertical-align: middle;"
        aria-hidden="true"
      >*</span></span>
      <i
        v-if="hasInfo"
        v-b-tooltip.hover
        :title="info"
        class="fal fa-info-circle mb-1 ml-2"
      />
    </template>
    <component
      :is="formComponent"
      v-if="type !== 'hidden'"
      v-model="internalValue"
      v-bind="$props"
      :state="state"
      :disabled="isDisabled"
      :planned-date-alert="plannedDateAlert"
      :class="[className, groupClass]"
      @input="handleInput"
      @blur="handleBlur"
      @change="handleInput"
    />
    <small
      v-if="note"
      class="form-text mt-1"
      style="color: #0c7fa8;"
    >
      <i class="fal fa-info-circle mr-1" />{{ note }}
    </small>
    <template slot="invalid-feedback">
      {{ invalidFeedback }}
    </template>
  </b-form-group>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { getInvalidCharacters } from '@/services/validation'
import Toggle from '@/components/common/Toggle'
import DatePicker from './elements/DatePicker'
import TimePicker from './elements/TimePicker'
import UploadField from './elements/UploadField'
import RadioField from './elements/RadioField'
import SelectField from './elements/SelectField'
import CheckboxField from './elements/CheckboxField'
import TextAreaField from './elements/TextAreaField'
import InputField from './elements/InputField'

export default {
  name: 'FormField',
  components: { Toggle, DatePicker, TimePicker, UploadField, RadioField, SelectField, CheckboxField, TextAreaField, InputField },
  mixins: [validationMixin],
  inject: ['registerFormField', 'unregisterFormField'],
  props: {
    value: {
      type: [String, Boolean, Number, Date, File, Array],
      default: undefined,
    },
    uuid: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    selectDisabled: {
      type: Boolean,
      default: false
    },
    locked: {
      type: Boolean,
      default: false
    },
    label: {
      type: [String, Object],
      default: '',
    },
    tabindex: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Field is always disabled
     */
    permaDisabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    // Output formatting
    format: {
      type: String,
      default: null,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    placeholder: {
      type: String,
      default: '',
    },
    validationRules: {
      type: [Object, Array],
      default: () => {
        return {}
      },
    },
    // Makes it possible to disable validation
    novalidate: {
      type: Boolean,
      default: false,
    },
    // Used by `type === select & radio`
    options: {
      type: Array,
      default: () => {
        return []
      },
    },
    // Used by `type === text`
    append: {
      type: String,
      default: '',
    },
    prepend: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 3,
    },
    min: {
      type: Number,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    // Used by `type === upload`
    browseLabel: {
      type: String,
      default: 'Browse',
    },
    dropInPlaceholder: {
      type: String,
      default: 'Drop file here...',
    },
    accept: {
      type: String,
      default: null,
    },
    // Info icon in label
    info: {
      type: [String, Object],
      default: null,
    },
    // Inline hint text rendered below the field
    note: {
      type: String,
      default: '',
    },
    infoHeader: {
      type: String,
      default: null,
    },
    infoHTML: {
      type: Boolean,
      default: false,
    },

    /**
     * Custom css
     */
    className: {
      type: String,
      default: '',
    },
    groupClass: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: null,
    },
    debounce: {
      type: [Number, String],
      default: '0',
    },
    preselected: {
      type: [String, Boolean, Number, Date, File, Array],
      default: '',
    },
    canReset: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: false
    },
    formatter: {
      type: Function,
      default: null
    },
    multiple: {
      type: Boolean,
      default: true
    },
    plannedDateAlert: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      originalValue: this.value,
      internalValue: this.value,
      blurred: false
    }
  },
  computed: {
    displayLabel() {
      return this.label?.[this.$i18n.locale] || this.label || ''
    },
    isRequired() {
      if (!this.validationRules) return false
      if (Array.isArray(this.validationRules)) return this.validationRules.includes('required')
      return 'required' in this.validationRules
    },
    isRequiredOnComplete() {
      if (!this.validationRules) return false
      if (Array.isArray(this.validationRules)) return this.validationRules.includes('requiredOnComplete')
      return 'requiredOnComplete' in this.validationRules
    },
    formComponent() {
      switch (this.type) {
        case 'toggle':
          return 'Toggle'
        case 'checkbox':
          return 'CheckboxField'
        case 'select':
          return 'SelectField'
        case 'radio':
          return 'RadioField'
        case 'textarea':
          return 'TextAreaField'
        case 'datepicker':
          return 'DatePicker'
        case 'timepicker':
          return 'TimePicker'
        case 'upload':
          return 'UploadField'
        case 'email':
          return 'InputField'
        case 'number':
          return 'InputField'
        case 'currency':
          return 'InputField'
        default:
          return 'InputField'
      }
    },
    state() {
      if (this.novalidate) {
        return null
      }
      return this.$v.internalValue.$dirty ? !this.$v.internalValue.$error : null
    },
    isDisabled() {
      return this.disabled || this.permaDisabled || this.locked
    },
    invalidFeedback() {
      let validator = this.$v.internalValue

      // Go over the validation rules, and return the
      // name of the first rule that is broken
      let match = Object.keys(this.validationRules)
        .find((rule) => {
          return !validator[rule]
        })

      if (match === -1) {
        return '' // apparently no rules are broken?
      }

      let params = validator.$params

      switch (match) {
        case 'email':
          return this.$i18n.t('Process.invalidEmail')
        case 'required':
          return this.$i18n.t('Process.mandatoryField')
        case 'minLength':
          return this.$i18n.t('Process.minLength', { minLength: params.minLength.min })
        case 'maxLength':
          return this.$i18n.t('Process.maxLength', { minLength: params.maxLength.max  })
        case 'isUniqueBuildingCode':
          return this.$i18n.t('Process.alreadyUsedPID')
        case 'multilineUnicodeString':
        case 'unicodeString':
          return `${this.$i18n.t('Process.invalidCharacters')} ${getInvalidCharacters(validator.$model)}`
      }

      return this.$i18n.t('Process.checkYourInput')
    },
    hasInfo() {
      return this.info
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.internalValue = this.type === 'number' && newValue ? parseInt(newValue, 10) : newValue
        this.internalValue = this.type === 'checkbox' ? this.toCheckboxArray(newValue) : newValue
      },
    },
  },
  created() {
    // On creation, set the original field value.
    this.originalValue = this.value

    // On creation, set the internal field value
    this.internalValue = this.type === 'number' && this.value ? parseInt(this.value, 10) : this.value
    this.internalValue = this.type === 'checkbox' ? this.toCheckboxArray(this.value) : this.internalValue

    if (!this.value && this.preselected !== '') {
      this.internalValue = this.preselected
      this.$nextTick(() => this.$emit('input', this.internalValue))
    }

    if (this.type === 'toggle') { // Toggle value as empty string or no property yet => boolean
      this.internalValue = !!this.value
    }

    // If contained within a Form component, register the form field
    if (this.registerFormField) {
      this.registerFormField(this)
    }
  },
  beforeDestroy() {
    if (this.unregisterFormField) {
      this.unregisterFormField(this)
    }
  },
  /**
   * Vuelidate validation rules. Set through the validationRules prop
   */
  validations() {
    return (this.validationRules)
      ? { internalValue: this.validationRules }
      : {}
  },
  methods: {
    handleReset() {
      this.$refs.datepicker.onResetButton()
      this.internalValue = 'Cancelled'
      this.$emit('input', this.internalValue)
    },
    getValue() {
      if (this.type === 'number') {
        return this.internalValue === '' ? 0 : parseInt(this.internalValue, 10)
      }
      if (this.format === 'float') {
        return this.internalValue === '' ? 0 : parseInt(this.internalValue, 10)
        // this.internalValue = this.internalValue.replace(',', '.') // Accept both , & .
        // return this.internalValue === '' ? 0 : parseFloat(this.internalValue)
      }
      if (this.type === 'datepicker' && this.internalValue === '') {
        return null
      }

      // Only return available values for radio and select types
      if (
        ['select', 'radio'].includes(this.type) &&
        !this.options.map(option => option.value !== undefined ? option.value : option).includes(this.internalValue)
      ) {
        return null
      }
      return this.internalValue
    },
    getName() {
      return this.name
    },

    // Start validation after initial blur
    handleBlur() {
      if (this.blurred === false) {
        this.validate()
      }
      this.blurred = true
      this.$emit('blur')
    },
    handleInput(value) {
      if (this.blurred || this.type === 'select') {
        !this.disabled && this.validate()
      }
      !this.disabled && this.$emit('input', value)
    },
    validate() {
      if (!this.novalidate) {
        this.$v.$touch()
      }
    },
    isValid() {
      // Not ideal, but otherwise form processing would cancel
      return this.novalidate ? true : !!this.state
    },
    resetValidation() {
      this.$v.$reset()
    },
    /**
     * Force reloading the field value
     */
    reloadFieldValue({ value }) {
      this.internalValue = this.type === 'number' && value ? parseInt(value, 10) : value
      this.internalValue = this.type === 'checkbox' ? this.toCheckboxArray(value) : this.internalValue
    },
    /**
     * Normalize a checkbox field's value into a clean array, dropping any
     * blank/nullish entries (e.g. legacy `['']` data) that would otherwise
     * be treated as a non-empty selection by validation.
     */
    toCheckboxArray(value) {
      if (Array.isArray(value)) {
        return value.filter(entry => entry !== '' && entry !== null && entry !== undefined)
      }
      return (value === undefined || value === null || value === '') ? [] : [value]
    },
    /**
     * Fields retain their attachement to the Form element when removed from the DOM
     */
    isDeleted() {
      return false
    },

    /**
     * Reset the field value to the original value
     */
    reset() {
      this.resetValidation()
      this.internalValue = this.type === 'number' && this.originalValue ? parseInt(this.originalValue, 10) : this.originalValue
      // TODO: emit input event (to let the parent component(s) know about the change)?
    },
  },
}
</script>

<style lang="scss">
.input-group input {
  min-height: 41px;
}

.input-group {
  .form-control {
    &:disabled {
      color: #6c757d;
      background: #f9f9f9;
    }
  }
}

.formfield {
  font-size: 16px;
  position: relative;

  input {
    min-height: 41px;

    &:disabled {
      color: black;
      background-color: #e9ecef;
    }
  }


  label,
  legend {
    color: #7F8FA4;
    padding-bottom: 0;
  }

  .info svg {
    max-height: 1rem;
    max-width: 1rem;
  }
}
</style>
